import {filter, fromEvent, race, Subject} from 'rxjs';
import {curriedQuerySelector, setRootCustomProperty} from '../dom-utils';
import {requestIdleCallback} from '../dom-utils/promises';
import getPlayback from '../libs/animation-playback';
import {equals} from 'ramda';

export const openedModal = new Subject();
export const closedModal = new Subject();

const CSS_MODAL_IS_VISIBLE = '--modal-visible';
const CSS_PAGE_HAS_SCROLL = '--page-has-scroll';
const CLASS_VISIBLE = 'is-visible';
const CLASS_CONTAINER = 'modal-container';
const CLASS_CLOSE = 'modal-close';
const CLASS_WRAPPER = 'modal-wrapper';
const CSS_MODAL_HAS_PROPERTY = '--modal-has-scroll';

/**
 * @typedef ModalOptions
 * @property {object} animation
 * @property {string} animation.enterClass
 * @property {string} animation.enterActiveClass
 * @property {string} animation.enterToClass
 * @property {string} animation.leaveClass
 * @property {string} animation.leaveActiveClass
 * @property {string} animation.leaveToClass
 */

const getContainer = curriedQuerySelector(`.${CLASS_CONTAINER}`);
const getWrapper = curriedQuerySelector(`.${CLASS_WRAPPER}`);
const getClose = curriedQuerySelector(`.${CLASS_CLOSE}`);

/**
 *
 * @param {HTMLElement} modal
 * @param {ModalOptions} options
 * @returns {(): void}
 */
const open = (modal, options) => async () => {
    const {
        animation: {
            leaveClass,
            leaveActiveClass,
            leaveToClass,
            enterClass,
            enterActiveClass,
            enterToClass,
        }
    } = options;
    const container = getContainer(modal);

    modal.style.setProperty('visibility', 'hidden');
    modal.classList.add(CLASS_VISIBLE);

    const animation = getPlayback(container, {
        class: enterClass,
        activeClass: enterActiveClass,
        toClass: enterToClass,
    });

    container.classList.remove(leaveClass, leaveActiveClass, leaveToClass);

    const {height: modalHeight} = container.getBoundingClientRect();
    const {scrollHeight: modalScrollHeight} = container;
    const {innerHeight: viewportHeight} = window;
    const {scrollHeight: documentHeight} = document.documentElement;

    if (modalScrollHeight > modalHeight)
        modal.style.setProperty(CSS_MODAL_HAS_PROPERTY, '1');

    if (documentHeight >= viewportHeight)
        setRootCustomProperty(CSS_PAGE_HAS_SCROLL, '1');

    document.body.classList.add('is-scroll-disabled');
    modal.style.removeProperty('visibility');
    container.style.setProperty('overflow', 'hidden');
    setRootCustomProperty(CSS_MODAL_IS_VISIBLE, '1');

    await requestIdleCallback();

    openedModal.next(modal);
    animation.play();

    await animation.done;

    modal.style.removeProperty(CSS_MODAL_HAS_PROPERTY);
    container.style.removeProperty('overflow');
    container.focus();
}

/**
 *
 * @param {HTMLElement} modal
 * @param {ModalOptions} options
 * @returns {(): void}
 */
const close = (modal, options) => async () => {
    const {
        animation: {
            enterToClass,
            enterActiveClass,
            enterClass,
            leaveClass,
            leaveActiveClass,
            leaveToClass,
        }
    } = options;
    const container = getContainer(modal);
    const animation = getPlayback(container, {
        class: leaveClass,
        activeClass: leaveActiveClass,
        toClass: leaveToClass,
    });

    container.classList.remove(enterClass, enterActiveClass, enterToClass);

    const {height} = container.getBoundingClientRect();
    const {scrollHeight} = container;

    if (scrollHeight > height)
        modal.style.setProperty(CSS_MODAL_HAS_PROPERTY, '1');

    container.style.setProperty('overflow', 'hidden');

    await requestIdleCallback();

    closedModal.next(modal);
    animation.play();

    await animation.done;

    modal.style.removeProperty(CSS_MODAL_HAS_PROPERTY);
    modal.classList.remove(CLASS_VISIBLE);
    container.style.removeProperty('overflow');
    setRootCustomProperty(CSS_MODAL_IS_VISIBLE, '0');
    setRootCustomProperty(CSS_PAGE_HAS_SCROLL, '0');
    document.body.classList.remove('is-scroll-disabled');
}

/**
 *
 * @param {HTMLElement} modal
 * @param {ModalOptions} options
 * @returns {Promise<[() => void, () => void]>}
 */
export default async function createModal(modal, options) {
    const openFn = open(modal, options);
    const closeFn = close(modal, options);

    const wrapper = getWrapper(modal);
    const clickOutside$ = fromEvent(wrapper, 'click')
        .pipe(filter(event => equals(event.target, wrapper)));

    const button = getClose(modal);
    const click$ = fromEvent(button, 'click');

    const container = getContainer(modal);
    const esc$ = fromEvent(container, 'keyup')
        .pipe(filter(event => equals(event.key, 'Escape')));

    race(clickOutside$, esc$, click$).subscribe(closeFn);

    return [openFn, closeFn];
}
